import { useEffect, useMemo, useRef, useState } from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from "material-react-table";
import { format } from "date-fns";
import {
  Add,
  Edit,
  FilterAltOff,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";

import {
  Box,
  Button,
  debounce,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  ListItem,
  ListItemText,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useAppSelector } from "../../../../hooks/store-hooks";
import { MRT_Localization_PT_BR } from "material-react-table/locales/pt-BR";
import { IProjetoTable } from "../../../../../application/models/geo-veles/IProjeto";
import { SkeletonVelesTable } from "../../../../components/skeleton-veles";
import { useProjetosService } from "../../../../../use-cases/geo-veles";
import LaunchIcon from "@mui/icons-material/Launch";
import { useUIService } from "../../../../../use-cases/ui";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import ptBR from "date-fns/locale/pt-BR";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AutoSizer from "react-virtualized-auto-sizer";
import { FixedSizeList } from "react-window";
import { formatCPF, formatCep } from "../../../../../utils";
import { RecorteCBOFilterPreview } from "../recortes-do-projeto/recortes-projeto-list/cbo";
import { RecorteClasseSocialFilterPreview } from "../recortes-do-projeto/recortes-projeto-list/classe-social";
import { RecorteContatoFilterPreview } from "../recortes-do-projeto/recortes-projeto-list/contato";
import { RecorteDataNascimentoFilterPreview } from "../recortes-do-projeto/recortes-projeto-list/data-nascimento";
import { RecorteEscolaridadeFilterPreview } from "../recortes-do-projeto/recortes-projeto-list/escolaridade";
import { RecorteEstadoCivilFilterPreview } from "../recortes-do-projeto/recortes-projeto-list/estado-civil";
import { RecorteIdadeFilterPreview } from "../recortes-do-projeto/recortes-projeto-list/idade";
import { RecorteLocalizacaoFilterPreview } from "../recortes-do-projeto/recortes-projeto-list/localizacao";
import { RecorteProfissaoFilterPreview } from "../recortes-do-projeto/recortes-projeto-list/profissao";
import { RecorteRendaAnualFilterPreview } from "../recortes-do-projeto/recortes-projeto-list/renda-anual";
import { RecorteRendaMensalFilterPreview } from "../recortes-do-projeto/recortes-projeto-list/renda-mensal";
import { RecorteScoreFilterPreview } from "../recortes-do-projeto/recortes-projeto-list/score";
import { RecorteSexoFilterPreview } from "../recortes-do-projeto/recortes-projeto-list/sexo";
import { ListasCidadesFilterPreview } from "../detalhes-do-projeto/filtros/localizacao/lista-cidades/forms/listaCidadesFilterPreview";
import { ITipoUsuario } from "../../../../../application/models";
import { ListasCboFilterPreview } from "../detalhes-do-projeto/filtros/cbo/forms/ListasCboFilterPreview";
import { useListaCboService } from "../../../../../use-cases/geo-veles/lista-cbo";
import { FormEditProjeto } from "./form-projeto/FormEditProjeto";
import { FormCreateProjeto } from "./form-projeto/FormCreateProjeto";

export const ListaProjetos = () => {
  // State
  const {
    projetos,
    projetosTotal,
    projetoListFiltros,
    projetosPagination,
    createProjetoForm,
  } = useAppSelector((state) => state.projetos);
  const { currentUser } = useAppSelector((state) => state.user);
  // Use State
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 5,
  });

  // Service
  const { setActivePage, changeTitleAndMetaDesc } = useUIService();
  const {
    setProjetos,
    setProjetosPagination,
    setOpenCreateProjetoForm,
    setOpenEditProjetoForm,
    openProjeto,
    setProjetoListFiltrosSearchTerm,
    setProjetoListFiltrosInclusaoStart,
    resetProjetoListFiltrosInclusaoStart,
    setProjetoListFiltrosInclusaoEnd,
    resetProjetoListFiltrosInclusaoEnd,
    resetProjetoListFiltrosSearchTerm,
    setProjetoListFiltrosAtivo,
    resetProjetoListFiltrosAtivo,
    setProjetoListFiltrosUsuario,
    resetProjetoListFiltrosUsuario,
    setProjetoListFiltrosLoginUsuario,
    resetProjetoListFiltrosLoginUsuario,
    resetProjetoListFiltros,
    setOpenDuplicateProjetoForm,
    setProjetoListFiltrosCliente,
    resetProjetoListFiltrosCliente,
    archiveProjeto,
  } = useProjetosService();

  const { setupListasCbo } = useListaCboService();

  const isFirstRender = useRef(false);
  const handleSearchTermChange = debounce((value: string) => {
    if (value.length > 1) {
      setProjetoListFiltrosSearchTerm(value);
    } else {
      resetProjetoListFiltrosSearchTerm();
    }
    setProjetosPagination({
      ...projetosPagination,
      page: 0,
    });
  }, Number(process.env.REACT_APP_TIMEOUT_DEBOUNCE));

  const [cpfSearchTerms, setCpfSearchTerms] = useState<{
    [key: string]: string;
  }>({});

  const [cepSearchTerms, setCepSearchTerms] = useState<{
    [key: string]: string;
  }>({});

  const formatCPFProgressivo = (cpf: string): string => {
    return cpf
      .replace(/\D/g, "")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  };

  const formatCepProgressivo = (cep: string): string => {
    return cep.replace(/\D/g, "").replace(/(\d{5})(\d{1,3})$/, "$1-$2");
  };

  const handleUsuarioChange = debounce((value: string) => {
    if (value.length > 1) {
      setProjetoListFiltrosUsuario(value);
    } else {
      resetProjetoListFiltrosUsuario();
    }
    setProjetosPagination({
      ...projetosPagination,
      page: 0,
    });
  }, Number(process.env.REACT_APP_TIMEOUT_DEBOUNCE));

  const handleLoginUsuarioChange = debounce((value: string) => {
    if (value.length > 1) {
      setProjetoListFiltrosLoginUsuario(value);
    } else {
      resetProjetoListFiltrosLoginUsuario();
    }
    setProjetosPagination({
      ...projetosPagination,
      page: 0,
    });
  }, Number(process.env.REACT_APP_TIMEOUT_DEBOUNCE));

  const handleClienteChange = debounce((value: string) => {
    if (value.length > 1) {
      setProjetoListFiltrosCliente(value);
    } else {
      resetProjetoListFiltrosCliente();
    }
    setProjetosPagination({
      ...projetosPagination,
      page: 0,
    });
  }, Number(process.env.REACT_APP_TIMEOUT_DEBOUNCE));

  const handleStatusChange = debounce(
    (event: any, newValue: { label: string; value: string } | null) => {
      if (!newValue) {
        resetProjetoListFiltrosAtivo();
      } else {
        setProjetoListFiltrosAtivo(newValue.value === "ativo");
      }
      setProjetosPagination({
        ...projetosPagination,
        page: 0,
      });
    },
    Number(process.env.REACT_APP_TIMEOUT_DEBOUNCE),
  );

  const columns = useMemo<MRT_ColumnDef<IProjetoTable>[]>(() => {
    const options = [
      {
        value: "ativo",
        label: "Ativo",
      },
      {
        value: "inativo",
        label: "Arquivado",
      },
    ];

    const columnsToSet: MRT_ColumnDef<IProjetoTable>[] = [
      {
        header: "Abrir",
        size: 50,
        enableColumnActions: false,
        Cell: ({ row }) => (
          <IconButton
            color="primary"
            size="small"
            disabled={
              currentUser?.IdVelesPerfilInterno !==
              ITipoUsuario.USUARIO_CLIENTE &&
              currentUser?.LoginUsuario !== row.original.LoginUsuario
            }
            onClick={() => {
              openProjeto(row.original.IdVelesGeoProjeto);
            }}
          >
            <LaunchIcon />
          </IconButton>
        ),
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: {
          align: "center",
        },
      },
      {
        header: "Editar",
        size: 50,
        enableColumnActions: false,
        Cell: ({ row }) => (
          <IconButton
            color="primary"
            size="small"
            disabled={
              currentUser?.IdVelesPerfilInterno !==
              ITipoUsuario.USUARIO_CLIENTE &&
              currentUser?.LoginUsuario !== row.original.LoginUsuario
            }
            onClick={() => {
              setOpenEditProjetoForm({
                open: true,
                idVelesGeoProjeto: row.original.IdVelesGeoProjeto,
              });
            }}
          >
            <Edit />
          </IconButton>
        ),
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: {
          align: "center",
        },
      },
      {
        header: "Duplicar Projeto",
        size: 50,
        enableColumnActions: false,
        Cell: ({ row }) => (
          <IconButton
            color="primary"
            size="small"
            onClick={() => {
              setOpenDuplicateProjetoForm({
                openForm: true,
                idVelesGeoProjeto: row.original.IdVelesGeoProjeto,
                nome: `${row.original.Nome} - CÓPIA`,
                idUsuario: Number(currentUser!.IdUsuario ?? 0),
              });
            }}
          >
            <ContentCopyIcon />
          </IconButton>
        ),
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: {
          align: "center",
        },
      },
      {
        accessorKey: "Nome",
        header: "Nome",
        size: 150,
        enableColumnActions: false,
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: {
          align: "center",
        },
        Filter: ({ column }) => (
          <TextField
            fullWidth
            placeholder="Buscar por nome"
            variant="outlined"
            size="small"
            onChange={(e) => handleSearchTermChange(e.target.value)}
          />
        ),
      },

      {
        accessorKey: "LoginCliente",
        header: "Login Cliente",
        size: 150,
        enableColumnActions: false,
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: {
          align: "center",
        },
        Filter: ({ column }) => (
          <TextField
            fullWidth
            placeholder="Buscar por Cliente"
            variant="outlined"
            size="small"
            onChange={(e) => handleClienteChange(e.target.value)}
          />
        ),
      },

      {
        accessorKey: "LoginUsuario",
        header: "Login Usuário",
        size: 150,
        enableColumnActions: false,
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: {
          align: "center",
        },
        Filter: ({ column }) => (
          <TextField
            fullWidth
            placeholder="Buscar por login de usuário"
            variant="outlined"
            size="small"
            onChange={(e) => handleLoginUsuarioChange(e.target.value)}
          />
        ),  
      },
      {
        accessorKey: "Inclusao",
        header: "Data de Criação",
        size: 150,
        enableColumnActions: false,
        accessorFn: (rowData: any) => {
          return new Date(rowData.Inclusao).getTime() + 3 * 60 * 60 * 1000;
        },
        Cell: ({ cell }) => (
          <span>{format(cell.getValue<Date>(), "dd/MM/yyyy HH:mm:ss")}</span>
        ),
        Filter: () => (
          <Grid style={{ display: "flex", gap: "8px" }}>
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              adapterLocale={ptBR}
            >
              <DatePicker
                label="Data Inicial"
                value={
                  projetoListFiltros.inclusaoStart
                    ? new Date(projetoListFiltros.inclusaoStart)
                    : null
                }
                onChange={debounce((newValue: Date | null, context) => {
                  if (context.validationError !== "invalidDate") {
                    if (newValue) {
                      setProjetoListFiltrosInclusaoStart(newValue);
                    } else {
                      resetProjetoListFiltrosInclusaoStart();
                    }
                  }
                }, 1000)}
                disableFuture
              />
            </LocalizationProvider>
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              adapterLocale={ptBR}
            >
              <DatePicker
                label="Data Final"
                value={
                  projetoListFiltros.inclusaoEnd
                    ? new Date(projetoListFiltros.inclusaoEnd)
                    : null
                }
                onChange={debounce((newValue: Date | null, context) => {
                  if (context.validationError !== "invalidDate") {
                    if (newValue) {
                      setProjetoListFiltrosInclusaoEnd(newValue);
                    } else {
                      resetProjetoListFiltrosInclusaoEnd();
                    }
                  }
                }, 1000)}
                disableFuture
              />
            </LocalizationProvider>
          </Grid>
        ),
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: {
          align: "center",
        },
      },
      {
        accessorKey: "Ativo",
        header: projetoListFiltros.ativo === false ? "Desarquivar" : "Arquivar",
        size: 100,
        enableColumnActions: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <IconButton
            color={row.original.Ativo ? "primary" : "secondary"}
            size="small"
            onClick={() => {
              archiveProjeto({
                idVelesGeoProjeto: row.original.IdVelesGeoProjeto,
                ativo: !row.original.Ativo,
                nome: row.original.Nome,
              });
            }}
          >
            {row.original.Ativo ? <Visibility /> : <VisibilityOff />}
          </IconButton>
        ),
        enableColumnFilter: false,
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: {
          align: "center",
        },
      },
    ];

    return columnsToSet;
  }, [
    openProjeto,
    setOpenEditProjetoForm,
    setOpenDuplicateProjetoForm,
    currentUser,
    handleSearchTermChange,
    handleLoginUsuarioChange,
    projetoListFiltros.inclusaoStart,
    projetoListFiltros.inclusaoEnd,
    setProjetoListFiltrosInclusaoStart,
    resetProjetoListFiltrosInclusaoStart,
    setProjetoListFiltrosInclusaoEnd,
    resetProjetoListFiltrosInclusaoEnd,
    projetoListFiltros.ativo,
    handleClienteChange,
    handleStatusChange,
    archiveProjeto,
  ]);

  const table = useMaterialReactTable({
    columns,
    data: projetos,
    columnFilterDisplayMode: "popover",
    manualFiltering: true,
    enableHiding: false,
    enableSorting: false,
    enableFullScreenToggle: false,
    enableGlobalFilter: false,
    enableColumnFilters: true,
    enableExpandAll: false,
    enableDensityToggle: false,
    localization: MRT_Localization_PT_BR,
    onPaginationChange: setPagination,
    state: {
      pagination,
      isLoading: loading,
    },
    rowCount: projetosTotal,
    manualPagination: true,

    displayColumnDefOptions: {
      "mrt-row-expand": {
        header: "Detalhes",
      },
    },

    enableExpanding: true,

    renderTopToolbarCustomActions: () => (
      <Box>
        <FormControlLabel
          control={
            <Switch
              checked={projetoListFiltros.ativo === false}
              onChange={(e) => {
                const isChecked = e.target.checked;
                setProjetoListFiltrosAtivo(!isChecked);
                setPagination((prev) => ({ ...prev, pageIndex: 0 }));
              }}
              color="primary"
            />
          }
          label={
            <Typography variant="body2" fontWeight="bold">
              {projetoListFiltros.ativo === false
                ? "VER ATIVOS"
                : "VER ARQUIVADOS"}
            </Typography>
          }
        />
      </Box>
    ),

    renderDetailPanel: ({ row }) => (
      <Box
        sx={{
          padding: 2,
          backgroundColor: "#fafafa",
          marginBottom: "16px",
          paddingBottom: "16px",
        }}
      >
        <Typography variant="subtitle1" fontWeight="bold">
          Detalhes do Projeto
        </Typography>

        <Typography variant="body2">
          <strong>Projeto:</strong> {row.original.Nome}
        </Typography>

        {/* Criado por */}
        <Typography variant="body2">
          <strong>Criado por:</strong> {row.original.NomeUsuario}
        </Typography>

        {/* Data Criação */}
        <Typography variant="body2">
          <strong>Data da criação do projeto:</strong>{" "}
          {format(
            new Date(
              new Date(row.original.Inclusao).getTime() + 3 * 60 * 60 * 1000,
            ),
            "dd/MM/yyyy    HH:mm:ss",
          )}
        </Typography>

        <Typography variant="body2">
          <strong>Atualizado em:</strong>{" "}
          {row.original.Atualizacao
            ? format(
              new Date(
                new Date(row.original.Atualizacao).getTime() +
                3 * 60 * 60 * 1000,
              ),
              "dd/MM/yyyy    HH:mm:ss",
            )
            : "Sem atualização"}
        </Typography>

        <br />

        <Typography
          variant="body1"
          color="primary"
          fontWeight="bold"
          textTransform="uppercase"
        >
          Filtros do Projeto
        </Typography>

        <Grid
          container
          spacing={0}
          sx={{ width: "100%", padding: " 0 16px", marginBottom: "-10px" }}
        >
          {/* Seção de Filtros */}
          <Grid item xs={12} md={12}>
            {row.original.Filtros &&
              Object.keys(row.original.Filtros).length > 0 ? (
              <>
                <br />
                <Grid container spacing={2} sx={{ marginBottom: 0 }}>
                  {[
                    typeof row.original.Filtros === "object" &&
                      row.original.Filtros !== null &&
                      "cbo" in row.original.Filtros &&
                      Array.isArray(row.original.Filtros.cbo) &&
                      row.original.Filtros.cbo.length > 0 ? (
                      <RecorteCBOFilterPreview
                        cboFilter={row.original.Filtros.cbo}
                        key="cbo"
                      />
                    ) : null,

                    typeof row.original.Filtros === "object" &&
                      row.original.Filtros !== null &&
                      "listaCBO" in row.original.Filtros &&
                      Array.isArray(row.original.Filtros.listaCBO) &&
                      row.original.Filtros.listaCBO.length > 0 ? (
                      <ListasCboFilterPreview
                        listasCboFilterPreview={row.original.Filtros.listaCBO}
                        key="listasCbo"
                      />
                    ) : null,

                    // (() => {

                    //   if (
                    //     typeof row.original.Filtros === "object" &&
                    //     row.original.Filtros !== null &&
                    //     "listaCBO" in row.original.Filtros &&
                    //     Array.isArray(row.original.Filtros.listaCBO) &&
                    //     row.original.Filtros.listaCBO.length > 0
                    //   ) {

                    //     // Expandir IDs para objetos completos
                    //     const listasCboCompletas = row.original.Filtros.listaCBO!
                    //       .map(id => {
                    //         const lista = listasCbo.find(lista => lista.IdVelesGeoListaCBO === id);
                    //         return lista;
                    //       })
                    //       .filter((lista): lista is IListaCbo => Boolean(lista));

                    //     return listasCboCompletas.length > 0 ? (
                    //       <ListasCboFilterPreview
                    //         listasCboFilterPreview={listasCboCompletas}
                    //         key="listasCbo"
                    //       />
                    //     ) : null;
                    //   }
                    //   return null;
                    // })(),

                    typeof row.original.Filtros === "object" &&
                      row.original.Filtros !== null &&
                      "classeSocial" in row.original.Filtros &&
                      Array.isArray(row.original.Filtros.classeSocial) &&
                      row.original.Filtros.classeSocial.length > 0 ? (
                      <RecorteClasseSocialFilterPreview
                        classeSocialFilter={row.original.Filtros.classeSocial}
                        key="classeSocial"
                      />
                    ) : null,
                    typeof row.original.Filtros === "object" &&
                      row.original.Filtros !== null &&
                      "contato" in row.original.Filtros &&
                      Array.isArray(row.original.Filtros.contato) &&
                      row.original.Filtros.contato.length > 0 ? (
                      <RecorteContatoFilterPreview
                        contatoFilter={row.original.Filtros.contato}
                        key="contato"
                      />
                    ) : null,
                    typeof row.original.Filtros === "object" &&
                      row.original.Filtros !== null &&
                      "dataNascimento" in row.original.Filtros &&
                      row.original.Filtros.dataNascimento ? (
                      <RecorteDataNascimentoFilterPreview
                        dataNascimentoFilter={
                          row.original.Filtros.dataNascimento
                        }
                        key="dataNascimento"
                      />
                    ) : null,
                    typeof row.original.Filtros === "object" &&
                      row.original.Filtros !== null &&
                      "escolaridade" in row.original.Filtros &&
                      Array.isArray(row.original.Filtros.escolaridade) &&
                      row.original.Filtros.escolaridade.length > 0 ? (
                      <RecorteEscolaridadeFilterPreview
                        escolaridadeFilter={row.original.Filtros.escolaridade}
                        key="escolaridade"
                      />
                    ) : null,
                    typeof row.original.Filtros === "object" &&
                      row.original.Filtros !== null &&
                      "estadoCivil" in row.original.Filtros &&
                      Array.isArray(row.original.Filtros.estadoCivil) &&
                      row.original.Filtros.estadoCivil.length > 0 ? (
                      <RecorteEstadoCivilFilterPreview
                        estadoCivilFilter={row.original.Filtros.estadoCivil}
                        key="estadoCivil"
                      />
                    ) : null,
                    typeof row.original.Filtros === "object" &&
                      row.original.Filtros !== null &&
                      "idade" in row.original.Filtros &&
                      row.original.Filtros.idade ? (
                      <RecorteIdadeFilterPreview
                        idadeFilter={row.original.Filtros.idade}
                        key="idade"
                      />
                    ) : null,

                    typeof row.original.Filtros === "object" &&
                      row.original.Filtros !== null &&
                      "profissao" in row.original.Filtros &&
                      Array.isArray(row.original.Filtros.profissao) &&
                      row.original.Filtros.profissao.length > 0 ? (
                      <RecorteProfissaoFilterPreview
                        profissaoFilter={row.original.Filtros.profissao}
                        key="profissao"
                      />
                    ) : null,
                    typeof row.original.Filtros === "object" &&
                      row.original.Filtros !== null &&
                      "rendaPresumidaAnual" in row.original.Filtros &&
                      row.original.Filtros.rendaPresumidaAnual ? (
                      <RecorteRendaAnualFilterPreview
                        rendaAnualFilter={
                          row.original.Filtros.rendaPresumidaAnual
                        }
                        key="rendaPresumidaAnual"
                      />
                    ) : null,
                    typeof row.original.Filtros === "object" &&
                      row.original.Filtros !== null &&
                      "rendaPresumidaMensal" in row.original.Filtros &&
                      row.original.Filtros.rendaPresumidaMensal ? (
                      <RecorteRendaMensalFilterPreview
                        rendaMensalFilter={
                          row.original.Filtros.rendaPresumidaMensal
                        }
                        key="rendaPresumidaMensal"
                      />
                    ) : null,
                    typeof row.original.Filtros === "object" &&
                      row.original.Filtros !== null &&
                      "scoreVeles" in row.original.Filtros &&
                      Array.isArray(row.original.Filtros.scoreVeles) &&
                      row.original.Filtros.scoreVeles.length > 0 ? (
                      <RecorteScoreFilterPreview
                        scoreVelesFilter={row.original.Filtros.scoreVeles}
                        key="scoreVeles"
                      />
                    ) : null,
                    typeof row.original.Filtros === "object" &&
                      row.original.Filtros !== null &&
                      "sexo" in row.original.Filtros &&
                      Array.isArray(row.original.Filtros.sexo) &&
                      row.original.Filtros.sexo.length > 0 ? (
                      <RecorteSexoFilterPreview
                        sexoFilter={row.original.Filtros.sexo}
                        key="sexo"
                      />
                    ) : null,

                    // Localização
                    typeof row.original.Filtros === "object" &&
                      row.original.Filtros !== null &&
                      "localizacao" in row.original.Filtros &&
                      Array.isArray(row.original.Filtros.localizacao) &&
                      row.original.Filtros.localizacao.length > 0 ? (
                      <RecorteLocalizacaoFilterPreview
                        localizacaoFilter={row.original.Filtros.localizacao}
                        key="localizacao"
                      />
                    ) : null,

                    // Listas de Cidades Detalhes
                    typeof row.original.Filtros === "object" &&
                      row.original.Filtros !== null &&
                      "listasCidades" in row.original.Filtros &&
                      Array.isArray(row.original.Filtros.listasCidades) &&
                      row.original.Filtros.listasCidades?.length > 0 ? (
                      <ListasCidadesFilterPreview
                        listasCidadesFilterPreview={
                          row.original.Filtros.listasCidades
                        }
                        key="listasCidades"
                      />
                    ) : null,

                    <Grid
                      item
                      xs={12}
                      sx={{
                        width: "100%",
                        marginLeft: "-16px",
                        marginBottom: "16px",
                      }}
                      key="listasDeduplicacao"
                    >
                      {typeof row.original.listasDeduplicacao === "object" &&
                        row.original.listasDeduplicacao !== null &&
                        "listasDeduplicacao" in row.original &&
                        Array.isArray(row.original.listasDeduplicacao) &&
                        row.original.listasDeduplicacao.length > 0 && (
                          <Grid container spacing={2}>
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="space-between"
                              width="100%"
                              sx={{ marginLeft: "16px", marginBottom: 1 }}
                            >
                              <Typography
                                variant="body1"
                                color="primary"
                                fontWeight="bold"
                                textTransform="uppercase"
                              >
                                Listas Deduplicação
                              </Typography>
                            </Box>

                            <br />
                            {row.original.listasDeduplicacao.map(
                              (lista, idx) => {
                                const termoBusca =
                                  cpfSearchTerms[
                                    lista.IdVelesGeoListaDeduplicacao ?? idx
                                  ]?.replace(/\D/g, "") || "";

                                const cpfsFiltrados = (lista.CPFs ?? []).filter(
                                  (cpf: string) => {
                                    return (
                                      !termoBusca ||
                                      cpf
                                        .replace(/\D/g, "")
                                        .includes(termoBusca)
                                    );
                                  },
                                );

                                return (
                                  <Grid
                                    item
                                    xs={12}
                                    md={6}
                                    key={
                                      lista.IdVelesGeoListaDeduplicacao ?? idx
                                    }
                                  >
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      justifyContent="space-between"
                                      mb={1}
                                    >
                                      <Typography
                                        fontWeight="bold"
                                        variant="subtitle2"
                                      >
                                        {lista.Nome}
                                      </Typography>
                                      <TextField
                                        size="small"
                                        placeholder="Buscar CPF"
                                        variant="outlined"
                                        onChange={(e) => {
                                          const raw = e.target.value.replace(
                                            /\D/g,
                                            "",
                                          );
                                          const masked =
                                            formatCPFProgressivo(raw);
                                          setCpfSearchTerms((prev) => ({
                                            ...prev,
                                            [lista.IdVelesGeoListaDeduplicacao ??
                                              idx]: masked,
                                          }));
                                        }}
                                        value={
                                          cpfSearchTerms[
                                          lista.IdVelesGeoListaDeduplicacao ??
                                          idx
                                          ] || ""
                                        }
                                        sx={{ ml: 2, minWidth: 180 }}
                                        InputProps={{
                                          startAdornment: (
                                            <InputAdornment position="start">
                                              <SearchIcon fontSize="small" />
                                            </InputAdornment>
                                          ),
                                        }}
                                      />
                                    </Box>
                                    <AutoSizer disableHeight>
                                      {({ width }: { width: number }) => (
                                        <FixedSizeList
                                          height={
                                            cpfsFiltrados.length <= 3
                                              ? 175
                                              : 250
                                          }
                                          style={{
                                            maxHeight: 150,
                                            background: "#fff",
                                          }}
                                          width={width}
                                          itemSize={46}
                                          itemCount={cpfsFiltrados.length}
                                          overscanCount={15}
                                        >
                                          {({ index, style }) => {
                                            const cpf = cpfsFiltrados[
                                              index
                                            ].replace(/\D/g, "");

                                            return (
                                              <ListItem key={index}>
                                                <ListItemText
                                                  primary={formatCPF(cpf)}
                                                />
                                              </ListItem>
                                            );
                                          }}
                                        </FixedSizeList>
                                      )}
                                    </AutoSizer>
                                  </Grid>
                                );
                              },
                            )}
                          </Grid>
                        )}
                    </Grid>,

                    <Grid
                      item
                      xs={12}
                      sx={{ width: "100%", marginLeft: "-16px" }}
                      key="listasCEP"
                    >
                      {typeof row.original.listasCEP === "object" &&
                        row.original.listasCEP !== null &&
                        "listasCEP" in row.original &&
                        Array.isArray(row.original.listasCEP) &&
                        row.original.listasCEP.length > 0 && (
                          <Grid container spacing={2}>
                            <Typography
                              variant="body1"
                              color="primary"
                              fontWeight="bold"
                              textTransform="uppercase"
                              sx={{ width: "100%", marginLeft: "16px" }}
                              mr={2}
                            >
                              Listas CEP
                            </Typography>
                            <br />
                            {row.original.listasCEP.map((lista, idx) => {
                              const termoBusca =
                                cepSearchTerms[
                                  lista.IdVelesGeoListaCEP ?? idx
                                ]?.replace(/\D/g, "") || "";

                              const cepsFiltrados = (lista.CEPs ?? [])
                                .filter(
                                  (cep: string) =>
                                    !termoBusca ||
                                    cep.replace(/\D/g, "").includes(termoBusca),
                                )
                                .map((cep: string) => formatCep(cep));

                              return (
                                <Grid
                                  item
                                  xs={12}
                                  md={6}
                                  key={lista.IdVelesGeoListaCEP ?? idx}
                                >
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    mb={1}
                                  >
                                    <Typography
                                      fontWeight="bold"
                                      variant="subtitle2"
                                    >
                                      {lista.Nome}
                                    </Typography>
                                    <TextField
                                      size="small"
                                      placeholder="Buscar CEP"
                                      variant="outlined"
                                      onChange={(e) => {
                                        const raw = e.target.value.replace(
                                          /\D/g,
                                          "",
                                        );
                                        const masked =
                                          formatCepProgressivo(raw);
                                        setCepSearchTerms((prev) => ({
                                          ...prev,
                                          [lista.IdVelesGeoListaCEP ?? idx]:
                                            masked,
                                        }));
                                      }}
                                      value={
                                        cepSearchTerms[
                                        lista.IdVelesGeoListaCEP ?? idx
                                        ] || ""
                                      }
                                      sx={{ ml: 2, minWidth: 180 }}
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                          </InputAdornment>
                                        ),
                                      }}
                                    />
                                  </Box>
                                  <AutoSizer disableHeight>
                                    {({ width }: { width: number }) => (
                                      <FixedSizeList
                                        height={
                                          cepsFiltrados.length <= 3 ? 175 : 250
                                        }
                                        style={{
                                          maxHeight: 150,
                                          background: "#fff",
                                        }}
                                        width={width}
                                        itemSize={46}
                                        itemCount={cepsFiltrados.length}
                                        overscanCount={15}
                                      >
                                        {({ index, style }) => (
                                          <ListItem style={style} key={index}>
                                            <ListItemText
                                              primary={cepsFiltrados[index]}
                                            />
                                          </ListItem>
                                        )}
                                      </FixedSizeList>
                                    )}
                                  </AutoSizer>
                                </Grid>
                              );
                            })}
                          </Grid>
                        )}
                    </Grid>,
                  ].filter(Boolean)}
                </Grid>
              </>
            ) : (
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ mt: 2, ml: 2, fontStyle: "italic" }}
              >
                Sem filtros para este projeto.
              </Typography>
            )}
          </Grid>
        </Grid>
      </Box>
    ),
  });

  useEffect(() => {
    setLoading(false);
  }, []);

  useEffect(() => {
    setupListasCbo();
  }, [setupListasCbo]);

  useEffect(() => {
    setProjetosPagination({
      limit: pagination.pageSize,
      page: pagination.pageIndex + 1,
    });
  }, [pagination, setProjetosPagination]);

  useEffect(() => {
    if (!isFirstRender.current) {
      setLoading(false);
      isFirstRender.current = true;
      setActivePage({ pageKey: "Geo-Veles/Projetos" });
    }
  }, [setActivePage]);

  useEffect(() => {
    setProjetos();
    console.log('projetos tsx >>>>>>>>>>>>>>>>>>>>>>\n', projetos)
  }, [setProjetos]);

  useEffect(() => {
    return () => {
      setProjetosPagination({ page: 0, limit: 5 });
      resetProjetoListFiltros();
    };
  }, [setProjetosPagination, resetProjetoListFiltros]);

  useEffect(() => {
    changeTitleAndMetaDesc({
      metaDesc: "Lista de projetos",
      title: "Veles_Geo - Lista de Projetos",
    });
  }, [changeTitleAndMetaDesc]);

  useEffect(() => { }, [resetProjetoListFiltros, setProjetosPagination]);

  return loading ? (
    <SkeletonVelesTable />
  ) : (
    <>
      <Typography color="primary" fontWeight="bold" variant="h6" gutterBottom>
        Lista de Projetos
      </Typography>
      <Divider
        style={{
          margin: "16px 0",
          borderBottomWidth: "2px",
          borderBottomColor: "rgba(0,0,0,0.5)",
        }}
      />

      <Grid container justifyContent={"space-between"} alignItems="flex-start">
        <Grid item>
          <Button
            style={{ marginBottom: "10px" }}
            variant="contained"
            color="primary"
            onClick={() => {
              setOpenCreateProjetoForm(true);
            }}
          >
            <Add style={{ marginRight: "8px" }} /> Novo Projeto
          </Button>
        </Grid>
        <Grid item>
          <Button
            disabled={
              projetoListFiltros.searchTerm === undefined &&
              projetoListFiltros.loginUsuario === undefined &&
              projetoListFiltros.usuario === undefined &&
              projetoListFiltros.loginCliente === undefined &&
              projetoListFiltros.inclusaoStart === undefined &&
              projetoListFiltros.inclusaoEnd === undefined &&
              projetoListFiltros.ativo === true
            }
            style={{ marginBottom: "10px" }}
            variant="contained"
            color="primary"
            onClick={() => {
              resetProjetoListFiltros();
              setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }}
          >
            <FilterAltOff style={{ marginRight: "8px" }} /> Limpar Filtros
          </Button>
        </Grid>
      </Grid>

      <MaterialReactTable table={table} />
      <FormEditProjeto />
      {createProjetoForm.open && <FormCreateProjeto />}
    </>
  );
};
